import { useEffect, useRef } from 'react';
import { useRouter, type RouterHistory } from '@tanstack/react-router';

/**
 * Closing the shell's open menus when the page changes (dashboard
 * Back/Forward): the mobile sidebar drawer, the workspace-tab menu, the
 * notification menus and the global search dropdown. The shell stays mounted
 * across pages, so without this a Back (mouse button, trackpad swipe, the top
 * bar arrow) would leave a menu open over the page it returned to.
 */

type HistoryActionType = Parameters<Parameters<RouterHistory['subscribe']>[0]>[0]['action']['type'];

/**
 * Whether an open shell menu closes after this history step. Every step that
 * shows a different screen closes it (a push, Back, Forward, a jump). A
 * replace on the same pathname does not: that is a page refining itself in the
 * background (a filter, a default choice, saved search text), and a menu the
 * person just opened should stay open through it.
 */
export function closesShellMenus(action: HistoryActionType, previousPathname: string, nextPathname: string): boolean {
  if (action !== 'REPLACE') return true;
  return previousPathname !== nextPathname;
}

/** Call `close` after every history step that changes the screen (see closesShellMenus). */
export function useCloseOnLocationChange(close: () => void): void {
  const router = useRouter();
  const closeRef = useRef(close);
  useEffect(() => {
    closeRef.current = close;
  });
  useEffect(() => {
    const { history } = router;
    let pathname = history.location.pathname;
    return history.subscribe(({ location, action }) => {
      const previous = pathname;
      pathname = location.pathname;
      if (closesShellMenus(action.type, previous, location.pathname)) closeRef.current();
    });
  }, [router]);
}
